// Detalhe de uma Configuração de pagamento: leitura da regra, ativar/desativar
// e edição da descrição. A regra não se edita: mudar a regra é criar outra.
import {
  ArrowLeftIcon,
  PencilIcon,
  PowerIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { DashboardContainer } from "@/components/layout/dashboard-container"
import { Header } from "@/components/layout/header"
import { Button } from "@/components/ui/button"
import { FRONTEND_ROUTES } from "@/constants"
import {
  DeactivatePaymentConfigurationDialog,
  EditPaymentConfigurationDescriptionDialog,
  feedbackVariantOf,
  PaymentConfigurationDetail,
  PaymentConfigurationDetailSkeleton,
  PaymentConfigurationFeedback,
  useCanWritePaymentConfigurations,
  usePaymentConfigurationDescriptionEdit,
  usePaymentConfigurationToggle,
} from "@/features/payment-configurations"
import { useWhiteLabels } from "@/hooks/use-white-labels"
import { usePaymentConfiguration } from "@/services/payment-configurations"

const prefix = "pages.paymentConfigurations"

export function PaymentConfigurationDetailPage() {
  const { t } = useTranslation()
  const { configId = "" } = useParams()
  const canWrite = useCanWritePaymentConfigurations()

  const {
    data: configuration,
    isLoading,
    isError,
    error,
    refetch,
  } = usePaymentConfiguration(configId)
  const { data: whiteLabels = [] } = useWhiteLabels()
  const toggle = usePaymentConfigurationToggle()
  const descriptionEdit =
    usePaymentConfigurationDescriptionEdit()

  const whiteLabelName = configuration
    ? (whiteLabels.find(
        (item) => item.uuid === configuration.whiteLabelId
      )?.name ?? configuration.whiteLabelId)
    : ""

  return (
    <DashboardContainer>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            { label: t("sidebar.administration") },
            {
              href: FRONTEND_ROUTES.PAYMENT_CONFIGURATIONS
                .href,
              label: t("sidebar.paymentConfigurations"),
            },
            { label: configId },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="page-title truncate">
              {configuration?.description ||
                t(`${prefix}.detail.title`)}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t(`${prefix}.detail.subtitle`)}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link
                to={
                  FRONTEND_ROUTES.PAYMENT_CONFIGURATIONS
                    .href
                }
              >
                <ArrowLeftIcon
                  className="mr-2 size-4"
                  aria-hidden="true"
                />
                {t(`${prefix}.detail.back`)}
              </Link>
            </Button>
            {canWrite && configuration && (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() =>
                  descriptionEdit.open(configuration)
                }
              >
                <PencilIcon
                  className="size-4"
                  aria-hidden="true"
                />
                {t(`${prefix}.actions.editDescription`)}
              </Button>
            )}
            {canWrite && configuration && (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={toggle.isToggling}
                onClick={() =>
                  toggle.handleToggle(configuration)
                }
              >
                <PowerIcon
                  className="size-4"
                  aria-hidden="true"
                />
                {configuration.active
                  ? t(`${prefix}.actions.deactivate`)
                  : t(`${prefix}.actions.activate`)}
              </Button>
            )}
          </div>
        </div>

        {/* 404 = não existe OU está fora do escopo de white labels do
            usuário — o core não distingue, de propósito. Acesso negado e 404
            não têm retry. */}
        {isError ? (
          <PaymentConfigurationFeedback
            variant={feedbackVariantOf(error)}
            onRetry={() => void refetch()}
          />
        ) : isLoading || !configuration ? (
          <PaymentConfigurationDetailSkeleton />
        ) : (
          <PaymentConfigurationDetail
            configuration={configuration}
            whiteLabelName={whiteLabelName}
          />
        )}

        <EditPaymentConfigurationDescriptionDialog
          configuration={descriptionEdit.editing}
          isSubmitting={descriptionEdit.isSubmitting}
          onSubmit={descriptionEdit.submit}
          onClose={descriptionEdit.close}
        />
        <DeactivatePaymentConfigurationDialog
          pending={toggle.pendingDeactivate}
          onConfirm={toggle.confirmDeactivate}
          onClose={toggle.closeDeactivate}
        />
      </section>
    </DashboardContainer>
  )
}
